<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Participantes</h1>
      <button class="btn-primary" @click="openCreate">+ Novo</button>
    </div>

    <!-- Filtros -->
    <div class="filters-bar card">
      <input class="input search-input" type="text" v-model="searchQuery" placeholder="Buscar por nome..." />
      <select class="input filter-select" v-model="statusFilter">
        <option value="all">Todos</option>
        <option value="pending">Pendentes</option>
        <option value="confirmed">Confirmados</option>
      </select>
    </div>

    <!-- Loading -->
    <SkeletonLoader v-if="store.loading" variant="table-row" :count="8" />

    <!-- Tabela -->
    <div class="table-card card" v-else-if="filteredItems.length > 0">
      <table class="table">
        <thead>
          <tr>
            <th>Nome</th>
            <th class="hide-mobile">Lider</th>
            <th class="hide-mobile">Cidade</th>
            <th>Status</th>
            <th>Acoes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredItems" :key="item.id">
            <td>
              <span class="name-cell">{{ item.name }}</span>
              <span class="sub-cell show-mobile">{{ item.leader }}</span>
            </td>
            <td class="hide-mobile">{{ item.leader }}</td>
            <td class="hide-mobile">{{ item.city || '-' }}</td>
            <td><StatusBadge :status="item.check ? 'confirmed' : 'pending'" /></td>
            <td>
              <div class="action-btns">
                <button class="btn-icon-sm btn-icon-sm-qr" @click="openQr(item)" title="QR Code">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M3 11h8V3H3v8zm2-6h4v4H5V5zm8-2v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm13-2h-2v3h-3v2h3v3h2v-3h3v-2h-3v-3zm-3 8v-3h-2v5h5v-2h-3zm6 0h-2v2h2v-2z"/></svg>
                </button>
                <button class="btn-icon-sm" @click="openEdit(item)" title="Editar">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83l3.75 3.75l1.83-1.83z"/></svg>
                </button>
                <button class="btn-icon-sm btn-icon-sm-danger" @click="confirmDelete(item.id)" title="Remover">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12Z"/></svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty -->
    <EmptyState
      v-else
      title="Nenhum participante"
      description="Cadastre o primeiro participante para comecar."
      actionLabel="+ Novo Participante"
      @action="openCreate"
    />

    <!-- Form Modal -->
    <ParticipantForm
      :visible="formVisible"
      :participant="editingParticipant"
      @save="handleSave"
      @cancel="formVisible = false"
    />

    <!-- QR Code Modal -->
    <div class="modal-overlay" v-if="qrParticipant" @click.self="qrParticipant = null">
      <div class="modal-card qr-modal">
        <h2 class="modal-title">{{ qrParticipant.name }}</h2>
        <p class="qr-instruction">Envie este QR Code para o participante via WhatsApp ou imprima.</p>
        <QrCodeDisplay :value="qrParticipant.id" :size="200" :label="qrParticipant.name" />
        <div class="qr-scan-url">
          <span class="label">Link para scanner</span>
          <code class="qr-url">{{ scanUrl }}</code>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="qrParticipant = null">Fechar</button>
        </div>
      </div>
    </div>

    <!-- Confirm Delete -->
    <ConfirmDialog
      :visible="deleteId !== ''"
      title="Remover participante?"
      message="Esta acao nao pode ser desfeita."
      confirmLabel="Remover"
      @confirm="handleDelete"
      @cancel="deleteId = ''"
    />
  </div>
</template>

<script>
import { useParticipantsStore } from '../stores/participants'
import ParticipantForm from '../components/ParticipantForm.vue'
import StatusBadge from '../components/ui/StatusBadge.vue'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'
import ConfirmDialog from '../components/ui/ConfirmDialog.vue'
import QrCodeDisplay from '../components/QrCodeDisplay.vue'

export default {
  name: 'ParticipantsPage',
  components: { ParticipantForm, StatusBadge, SkeletonLoader, EmptyState, ConfirmDialog, QrCodeDisplay },
  data() {
    return {
      searchQuery: '',
      statusFilter: 'all',
      formVisible: false,
      editingParticipant: null,
      deleteId: '',
      qrParticipant: null
    }
  },
  computed: {
    store() { return useParticipantsStore() },
    scanUrl() { return `${window.location.origin}/scan` },
    filteredItems() {
      let items = this.store.participants
      if (this.statusFilter === 'pending') items = items.filter(i => !i.check)
      if (this.statusFilter === 'confirmed') items = items.filter(i => i.check)
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        items = items.filter(i => i.name.toLowerCase().includes(q))
      }
      return items
    }
  },
  methods: {
    openQr(participant) {
      this.qrParticipant = { ...participant }
    },
    openCreate() {
      this.editingParticipant = null
      this.formVisible = true
    },
    openEdit(participant) {
      this.editingParticipant = { ...participant }
      this.formVisible = true
    },
    async handleSave(data) {
      try {
        if (this.editingParticipant) {
          await this.store.update(this.editingParticipant.id, data)
          this.$toast.open({ message: 'Participante atualizado', type: 'success', position: 'top-right', duration: 3000 })
        } else {
          await this.store.create(data)
          this.$toast.open({ message: 'Participante cadastrado', type: 'success', position: 'top-right', duration: 3000 })
        }
        this.formVisible = false
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao salvar'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 5000 })
      }
    },
    confirmDelete(id) {
      this.deleteId = id
    },
    async handleDelete() {
      try {
        await this.store.remove(this.deleteId)
        this.$toast.open({ message: 'Participante removido', type: 'info', position: 'top-right', duration: 3000 })
      } catch {
        this.$toast.open({ message: 'Erro ao remover', type: 'error', position: 'top-right', duration: 5000 })
      }
      this.deleteId = ''
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-base);
}
.page-header .page-title { margin-bottom: 0; }
.filters-bar {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-base);
  margin-bottom: var(--spacing-base);
}
.search-input { flex: 1; }
.filter-select { max-width: 160px; }
.table-card { padding: 0; overflow-x: auto; }
.table {
  width: 100%;
  border-collapse: collapse;
}
.table th {
  text-align: left;
  padding: 12px var(--spacing-base);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table td {
  padding: 14px var(--spacing-base);
  font-size: var(--text-base);
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-bg-separator);
}
.table tr:last-child td { border-bottom: none; }
.table tr:hover td { background: var(--color-bg-hover); }
.name-cell { font-weight: 600; }
.sub-cell {
  display: none;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
.action-btns { display: flex; gap: var(--spacing-sm); }
.btn-icon-sm {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-bg-hover);
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
}
.btn-icon-sm-qr:hover { background: #9b59b6; color: #fff; }
.btn-icon-sm:hover { background: var(--color-accent); color: #fff; }
.btn-icon-sm-danger:hover { background: var(--color-danger); color: #fff; }
@media (max-width: 600px) {
  .hide-mobile { display: none; }
  .sub-cell.show-mobile { display: block; }
  .filters-bar { flex-direction: column; }
  .filter-select { max-width: 100%; }
}
.qr-modal { max-width: 400px; text-align: center; }
.qr-instruction {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin-bottom: var(--spacing-base);
}
.qr-scan-url {
  margin-top: var(--spacing-base);
  text-align: left;
}
.qr-url {
  display: block;
  padding: 8px 12px;
  background: var(--color-bg-hover);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  word-break: break-all;
  margin-top: var(--spacing-sm);
}
</style>
